import { AccordionListItem } from '../../common/types/accordion-list-item';
import { aboutPickupSupportUrl } from './support-urls';

const INVENTORY_URL = '/inventory';

interface MarketSpecificFAQs {
  [key: string]: AccordionListItem[];
}
export const faqs: AccordionListItem[] = [
  {
    title: "$0 down, cancel anytime, cheaper than leasing or buying - what's the catch?",
    content: [
      {
        body: 'There is no catch!',
      },
      {
        body: "We ditched the traditional dealership model which has extra costs baked in. No fancy showrooms, no pushy salespeople, and no hidden fees. Instead, we're all about efficiency. We do everything online, from browsing to ordering your car. We negotiate to get the best deals on our vehicles, insurance, and service costs so that we can pass along those savings to you. You get to skip the hassle of the dealership and save money while you're at it.",
      },
      {
        body: "So yeah, zero down, lower monthly payments, and you can cancel anytime. It's a no-brainer!",
      },
    ],
  },
  {
    title: 'How is Flexcar different from a traditional lease?',
    content: [{ body: 'Flexcar is the only $0 down, month-to-month car lease.' }],
    list: [
      'Zero down payment',
      'Cancel anytime',
      'No sneaky dealership fees',
      'Keep the car for as long as you want',
      '24/7 roadside assistance and insurance is included',
      'Ability to swap cars',
    ],
  },
  {
    title: 'How can Flexcar help me avoid the sneaky hidden dealership fees?',
    content: [
      {
        body: "We hate fees as much as you do. With Flexcar, you'll never pay any hidden dealership fees. Say goodbye to documentation fees, dealer prep fees, financing fees, extended warranties, title fees, registration fees, destination fees, advertising fees, VIN etching fees, and all the other sneaky fees that you may encounter at a traditional dealership.",
      },
      {
        body: "Now, here's the deal. Sometimes we might need to charge a fee for specific services, like swapping to a different vehicle. We'll always be upfront and transparent with you about any charges. No surprises, just straight talk.",
      },
    ],
  },
  {
    title: "What's included with my Flexcar?",
    content: [
      {
        body: 'Your monthly payment covers everything, even discounts on gas! Flexcar provides insurance, covers routine vehicle maintenance, offers 24/7 roadside assistance and fuel discounts with Gas Saver™. Enjoy the convenience of driving without the burdens of traditional car ownership. ',
      },
    ],
  },
  {
    title: 'Is there really no down payment?',
    content: [
      { body: "Yup! There's never a down payment with Flexcar." },
      {
        body: 'Down payments in traditional car financing are non-refundable. In some cases, Flexcar may require a fully refundable security deposit. This security deposit is refunded when the vehicle is returned without damage, unpaid charges, mileage overage, or excess wear and tear.',
      },
    ],
  },
  {
    title: 'Can I cancel anytime?',
    content: [
      {
        body: "Yes, with Flexcar there are no long-term contracts. Flexcar offers monthly flexibility. We want Flexcar to be the only car you'll need. But hey, we get it—things change. So, if you need to make a change, we're here to support you every step of the way.",
      },
    ],
  },
  {
    title: 'Can I swap to another car anytime?',
    content: [
      {
        body: "For sure! You've got the green light to swap your current Flexcar for any other ride that's available. Just keep in mind, there's a small swap fee to cover the costs of prepping and processing the switch.",
      },
    ],
  },
  {
    title: 'Is maintenance covered by Flexcar?',
    content: [
      {
        body: "Absolutely! You can breathe easy knowing that Flexcar has your back when it comes to maintenance. We'll handle all the routine maintenance costs, so you can focus on enjoying your Flexcar worry-free. No more surprise maintenance or repair bills.",
      },
    ],
  },
  {
    title: "How do I know if I'm eligible?",
    content: [
      {
        body: "We'll determine your eligibility based on an identity and soft credit check performed during the checkout process.",
      },
      { body: 'Flexcar has the following minimum eligibility requirements:' },
    ],
    list: [
      '21+ years old',
      "Valid US or US Territory Driver's license",
      'A verified email address',
      'Consent to a soft credit check which will never impact your credit score',
    ],
  },
  {
    title: 'Does my existing car insurance / credit card cover my Flexcar?',
    content: [
      {
        body: "No, they do not. At Flexcar, basic liability insurance is always included, and selecting one of our protection plans is required. Supplementary coverage from existing personal auto insurance and credit card auto insurance only cover short-term use cases like rental cars. They do not generally cover your Flexcar vehicle, which is a longer-term car ownership replacement. Don't worry — we've got your back.",
      },
    ],
  },
  {
    title: 'How do I sign up?',
    content: [
      {
        body: "Signing up for Flexcar is super easy and quick! Here's how you can get started:",
      },
      {
        title: 'Find Your Car',
        body: 'Browse our selection of 2019-2024 models, including SUVs, sedans, and electric vehicles. Choose the one that fits your style and needs.',
      },
      {
        title: 'Choose Your Plan',
        body: 'Pick a monthly plan that works for you. No long-term commitments here – you can change your plan anytime. Your annual membership fee of $249, plus your monthly payment, covers everything: insurance, maintenance, 24/7 roadside assistance, registration, inspections, and a monthly car wash.',
        link: {
          text: 'View Cars',
          url: INVENTORY_URL,
          reloadDocument: true,
        },
      },
      {
        title: 'Pickup or Delivery',
        body: "Decide how you want to get your Flexcar. You can either pick it up at our location or have it delivered to you. We'll guide you through all the features so you're ready to roll.",
      },
      {
        title: 'Enjoy Your Flexcar',
        body: 'Hit the road with confidence, knowing that all the essentials are taken care of. Enjoy the convenience and freedom of your Flexcar.',
      },
      {
        body: "And that's it! If you have any questions or need help along the way, our support team is always here for you. Happy driving! 🚗",
      },
    ],
  },
  {
    title: 'What is Flexcar Gas Saver™?',
    content: [
      {
        body: 'Flexcar Gas Saver is one of our exclusive membership benefits! As a Flexcar member, you are automatically enrolled in the program that saves you 20-30 cents at participating gas stations near you. Nothing you need to do, just fill up at a station and see those savings roll in!',
      },
    ],
  },
];

export const homepageFaqs = faqs.slice(0, 4);

export const pickupSpecificFaqs: AccordionListItem[] = [
  {
    title: 'How do I pick up the car?',
    content: [
      {
        body: "You'll be able to schedule the date and time to pick up your car from a self-service location near you. Download the app to confirm all the details and unlock the car when it's time for pickup.",
      },
    ],
    link: {
      text: 'Learn more about how car pickup works',
      url: { aboutPickupSupportUrl },
    },
  },
];

export const deliverySpecificFaqs: AccordionListItem[] = [
  {
    title: 'Where can I get the car?',
    content: [
      {
        body: 'You have 2 options to get your car: pickup and delivery. For pickup, go to one of our easy-to-access parking lots and use the Flexcar app to find and unlock your car. For delivery, simply receive your car at a time and place of your choice. Please note that delivery is only available in some areas.',
      },
    ],
    link: {},
  },
];

export const marketSpecificFaqs: MarketSpecificFAQs = {};
